'use client';

/**
 * ContextRail
 *
 * Sticky right-edge contextual panel for resource-detail pages. Shows
 * read-mostly contextual data (audit activity, messages, related items)
 * scoped to the current entity in scope.
 *
 * Sibling to `SplitPanel`, NOT built on top of it — `SplitPanel` is for
 * create/edit (modal-y, ephemeral); `ContextRail` is persistent and read-mostly.
 *
 * Responsive:
 * - >=1280px: inline split (panel sits flush to viewport edge, fixed-positioned)
 * - 768-1279px: overlay (panel floats over content with backdrop)
 * - <768px:    full-page drawer (no thin vertical strip)
 *
 * Persistence:
 * - Open/closed state stored in localStorage keyed by `resourceKey`
 * - Auto-opens once per `resourceKey` (first-visit memory), then respects
 *   the user's collapse choice
 *
 * Issue #1472
 */

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, IconButton, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useLayoutOffset, useReducedMotion } from '../../hooks';
import { glassEffects } from '../../theme/shadows';
import { fontFamilies } from '../../theme/typography';
import { GlassTooltip } from '../GlassTooltip';
import { GlossyMenu, GlossyMenuItem } from '../GlossyMenu/GlossyMenu';
import { SplitPanel } from '../SplitPanel/SplitPanel';
import { SplitPanelContent } from '../SplitPanel/SplitPanelContent';
import { SplitPanelHeader } from '../SplitPanel/SplitPanelHeader';
import { useOptionalContextRail } from './ContextRailProvider';

// ============================================================================
// Types
// ============================================================================

export interface ContextRailTab {
  /** Unique tab id (used in storage key for last-active tab) */
  id: string;
  /** Label rendered both vertically (collapsed) and horizontally (expanded) */
  label: string;
  /** Icon rendered alongside label. Pass a Material icon node. */
  icon: React.ReactNode;
  /** Optional count badge — appears next to label when > 0 */
  count?: number;
  /** Optional aria-label override (defaults to `label`) */
  ariaLabel?: string;
}

/**
 * Identity scope for the rail's persisted state. Combined with
 * `resourceKey` to namespace localStorage entries by business + user so a
 * one-user preference on one tenant doesn't silently bleed into another
 * tenant or another logged-in user on the same browser (PR #1810 review
 * C3). Mirrors the same scoping pattern used for `emptyHomeSkipKey` on
 * the business-portal dashboard.
 *
 * Callers in pages that have `useAuth()` / `useBusiness()` available
 * SHOULD pass scope. When `scope` is omitted, the rail falls back to the
 * legacy unscoped key for backward compat — it still works, but state
 * leaks across tenants/users on shared browsers, and a `console.warn` is
 * emitted once per resourceKey in dev mode.
 */
export interface ContextRailScope {
  businessId?: string | null;
  userId?: string | null;
}

export interface ContextRailProps {
  /**
   * Stable per-resource key (e.g. `case:<caseId>`). Open/closed state
   * is persisted under this key. First visit auto-opens once.
   */
  resourceKey: string;
  /** Tab definitions (3-5 recommended) */
  tabs: ContextRailTab[];
  /** Tab id rendered when no last-active is stored */
  defaultTab?: string;
  /** Render function for tab content */
  children: (activeTabId: string) => React.ReactNode;
  /** ARIA label for the panel itself */
  ariaLabel?: string;
  /** Width of the expanded panel (px). Defaults to 360. */
  width?: number;
  /**
   * When true, the rail bypasses its internal open-state and renders only
   * the collapsed strip (or nothing on mobile). Use this from a parent that
   * has another right-anchored surface — e.g. a `<SplitPanel>` for create/
   * edit on the same page — so the two don't compete for `right: 0`. The
   * rail re-asserts its persisted open-state once `forceCollapsed` flips
   * back to false.
   */
  forceCollapsed?: boolean;
  /**
   * Identity scope used to namespace persisted state by business + user.
   * See `ContextRailScope` docs above. Strongly recommended in any
   * authenticated portal page; omit only for pre-auth surfaces where no
   * tenant data is available.
   */
  scope?: ContextRailScope;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_WIDTH = 360;
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 600;
const COLLAPSED_RAIL_WIDTH = 44;
const STORAGE_PREFIX = 'opsmatrix.contextRail';
const OPEN_PREFIX = `${STORAGE_PREFIX}.open`;
const ACTIVE_TAB_PREFIX = `${STORAGE_PREFIX}.activeTab`;
// SplitPanel persists its width as `split-view-width-<storageKey>`. The
// storageKey we hand it MUST stay shaped as `context-rail-<resourceKey>`
// (legacy unscoped) or `context-rail-<resourceKey>.<scope>` (scoped) so
// the read-side helper below can mirror what SplitPanel wrote.
const SPLIT_PANEL_STORAGE_KEY_PREFIX = 'context-rail-';

// Track resourceKeys we've already warned about so the unscoped-fallback
// console.warn fires once per key per session, not on every render.
const unscopedWarnedKeys = new Set<string>();

/**
 * Build a tenant-scoped suffix for the rail's localStorage keys. Returns
 * `<businessId>:<userId>` when scope is provided (with sane fallbacks
 * for the still-bootstrapping case), or `null` when scope is omitted —
 * the caller falls back to the legacy unscoped key in that case.
 */
function scopeSuffix(scope: ContextRailScope | undefined): string | null {
  if (!scope) return null;
  const business = scope.businessId ?? 'no-business';
  const user = scope.userId ?? 'anon';
  return `${business}:${user}`;
}

/**
 * Compose the full storage key. When scope is omitted, returns the
 * legacy unscoped key and (in dev mode, once per resourceKey) warns
 * that state will leak across tenants/users on a shared browser.
 */
function buildStorageKey(prefix: string, resourceKey: string, scope: ContextRailScope | undefined): string {
  const suffix = scopeSuffix(scope);
  if (suffix) return `${prefix}.${resourceKey}.${suffix}`;
  if (
    typeof process !== 'undefined' &&
    process.env?.['NODE_ENV'] !== 'production' &&
    !unscopedWarnedKeys.has(resourceKey)
  ) {
    unscopedWarnedKeys.add(resourceKey);
    // eslint-disable-next-line no-console
    console.warn(
      `[ContextRail] resourceKey="${resourceKey}" mounted without \`scope\`. Persisted open/active-tab state will leak across tenants and users on shared browsers. Pass scope={{ businessId, userId }} from useBusiness()/useAuth().`,
    );
  }
  return `${prefix}.${resourceKey}`;
}

// ============================================================================
// Storage helpers
// ============================================================================

function readBool(key: string): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return null;
  } catch {
    return null;
  }
}

function writeBool(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // Quota or disabled storage — silently ignore; the rail still works
    // session-locally, just without persistence.
  }
}

function readString(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeString(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

// ============================================================================
// Component
// ============================================================================

export function ContextRail({
  resourceKey,
  tabs: staticTabs,
  defaultTab,
  children,
  ariaLabel = 'Context panel',
  width = DEFAULT_WIDTH,
  forceCollapsed = false,
  scope,
}: ContextRailProps): React.ReactElement | null {
  const theme = useTheme();
  const reduceMotion = useReducedMotion();
  const animate = !reduceMotion;
  const isDark = theme.palette.mode === 'dark';
  const isMaterial = theme.designGuideline === 'material';
  const { offset: headerOffset } = useLayoutOffset();
  const topOffset = `${headerOffset}px`;

  // Dynamic tabs added via `useContextRail().openTab(...)` from anywhere
  // on the page. Static tabs first, then dynamic — preserves a stable
  // visual order. When no provider is mounted, `provider` is null and
  // dynamicTabs is empty (the rail still works as a static-tab-only
  // surface, which is the v1 behavior).
  const provider = useOptionalContextRail();
  const dynamicTabs = provider?.dynamicTabs ?? [];
  const tabs: ContextRailTab[] = [...staticTabs, ...dynamicTabs];

  // Map of dynamic tab id → content node so we can render dynamic tabs
  // without going through the `children` render-prop (which only knows
  // about static tabs).
  const dynamicContentById = new Map<string, React.ReactNode>(dynamicTabs.map((t) => [t.id, t.content]));
  const closableIds = new Set<string>(dynamicTabs.filter((t) => t.closable !== false).map((t) => t.id));

  // Mobile threshold for the FAB / drawer branch (SplitPanel handles
  // tablet overlay vs desktop inline internally — we only need to know
  // when to switch into the full-screen drawer mode).
  const isMobile = useMediaQuery('(max-width: 767.95px)');

  // Storage keys are tenant+user scoped via `scope` so a one-user
  // preference on Tenant 1 doesn't silently bleed into Tenant 2 or
  // another logged-in user on the same browser (PR #1810 review C3).
  // `buildStorageKey` falls back to the legacy unscoped key when
  // `scope` is omitted (with a one-time dev-mode warning), so existing
  // callers that haven't migrated yet keep working.
  const activeTabStorageKey = buildStorageKey(ACTIVE_TAB_PREFIX, resourceKey, scope);
  const openStorageKey = buildStorageKey(OPEN_PREFIX, resourceKey, scope);
  // SplitPanel storage key — its own helper prepends `split-view-width-`,
  // so the suffix we hand it must include the scope inline. Legacy
  // (unscoped) callers continue to land on `split-view-width-context-rail-<rk>`
  // for byte-perfect backward compatibility with already-written widths.
  const scopeSfx = scopeSuffix(scope);
  const splitPanelStorageKey = scopeSfx
    ? `${SPLIT_PANEL_STORAGE_KEY_PREFIX}${resourceKey}.${scopeSfx}`
    : `${SPLIT_PANEL_STORAGE_KEY_PREFIX}${resourceKey}`;
  const splitViewWidthKey = `split-view-width-${splitPanelStorageKey}`;

  // Active tab — defaults to last-saved or `defaultTab` or first tab
  const [activeTabId, setActiveTabId] = useState<string>(() => {
    const stored = readString(activeTabStorageKey);
    if (stored && tabs.some((t) => t.id === stored)) return stored;
    if (defaultTab && tabs.some((t) => t.id === defaultTab)) return defaultTab;
    return tabs[0]?.id ?? '';
  });

  // Open/closed state. Defaults closed on every breakpoint (PR #1477
  // design feedback) — the collapsed strip is discoverable enough on its
  // own and auto-opening on first visit was disruptive. After the user
  // expands once, that choice is persisted under `resourceKey` and
  // honoured on subsequent visits.
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return readBool(openStorageKey) ?? false;
  });

  // Live width — driven by the SplitPanel resize handle. Seeded from the
  // SplitPanel's own localStorage key so a refresh restores the user's
  // last drag, and clamped to [MIN, MAX] so a stale stored value can't
  // wedge the rail into an invalid state. SplitPanel storage key matches
  // ours below so width is also tenant+user scoped.
  const [currentWidth, setCurrentWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return width;
    try {
      const raw = window.localStorage.getItem(splitViewWidthKey);
      const parsed = raw ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(parsed) && parsed >= MIN_PANEL_WIDTH && parsed <= MAX_PANEL_WIDTH) return parsed;
    } catch {
      // ignore
    }
    return width;
  });

  // Header tab-switcher dropdown anchor. Replaces the old horizontal
  // tab bar with a SplitPanel-style title + dropdown.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // When the provider opens a dynamic tab via `openTab(...)`, it sets
  // `provider.activeTabId`. Mirror that into local state and force-expand
  // the rail so the new tab is visible.
  useEffect(() => {
    if (provider?.activeTabId && provider.activeTabId !== activeTabId) {
      setActiveTabId(provider.activeTabId);
      setIsOpen(true);
    }
    // We intentionally only watch provider.activeTabId — local state changes
    // shouldn't trigger this branch.
  }, [provider?.activeTabId]);

  // If the active tab is a dynamic tab that just got closed, fall back to
  // the page's default tab (or the first available).
  useEffect(() => {
    const stillExists = tabs.some((t) => t.id === activeTabId);
    if (!stillExists && tabs.length > 0) {
      setActiveTabId(defaultTab && tabs.some((t) => t.id === defaultTab) ? defaultTab : tabs[0]!.id);
    }
  }, [tabs, activeTabId, defaultTab]);

  // Persist active tab whenever it changes (only for static tabs — dynamic
  // tabs are session-scoped and shouldn't restore on next visit).
  useEffect(() => {
    if (staticTabs.some((t) => t.id === activeTabId)) {
      writeString(activeTabStorageKey, activeTabId);
    }
  }, [activeTabId, activeTabStorageKey, staticTabs]);

  // Persist open/closed whenever it changes.
  useEffect(() => {
    writeBool(openStorageKey, isOpen);
  }, [isOpen, openStorageKey]);

  // Emit the rail's current horizontal footprint as a CSS custom property
  // on the document root so consumer pages can opt-in to right-padding via
  // `pr: 'var(--context-rail-width, 0px)'`. Without this, the page's
  // rightmost 44px (collapsed) or 360px (expanded) is hidden under the
  // fixed-positioned rail. Cleared on unmount so other surfaces don't
  // inherit the inset. Mobile drawer covers the full viewport — no
  // horizontal inset needed there. ────────────────────────────────────
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    let railWidth: number;
    if (tabs.length === 0 || isMobile) {
      // No rail rendered (or drawer mode — no horizontal inset needed)
      railWidth = 0;
    } else if (forceCollapsed) {
      railWidth = COLLAPSED_RAIL_WIDTH;
    } else if (isOpen) {
      // Open: the panel sits to the LEFT of a persistent 44px tab strip, so
      // the page's right inset must reserve room for BOTH. Use the live
      // (possibly drag-resized) panel width so page padding tracks the
      // user's drag instead of locking to the prop default.
      railWidth = currentWidth + COLLAPSED_RAIL_WIDTH;
    } else {
      railWidth = COLLAPSED_RAIL_WIDTH;
    }
    root.style.setProperty('--context-rail-width', `${railWidth}px`);
    return () => {
      root.style.removeProperty('--context-rail-width');
    };
  }, [isOpen, isMobile, tabs.length, currentWidth, forceCollapsed]);

  const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  // ── ARIA tabs pattern wiring (Issue #1472 PR #1477 review C3) ─────────
  // Stable, sanitised prefix so multiple ContextRails on one page get
  // unique tab/tabpanel ids. resourceKey can contain colons/slashes etc.
  const idPrefix = `context-rail-${resourceKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const tabId = (tabKey: string) => `${idPrefix}-tab-${tabKey}`;
  const panelId = (tabKey: string) => `${idPrefix}-panel-${tabKey}`;

  // Keyboard nav per WAI-ARIA tabs pattern: ←/→ moves focus, Home/End
  // jumps to first/last. Vertical orientation in the collapsed strip uses
  // ↑/↓ instead. The tablist parent owns the handler so individual tabs
  // don't need to coordinate.
  const handleTabsKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>, orientation: 'horizontal' | 'vertical') => {
      const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
      const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
      const currentIdx = tabs.findIndex((t) => t.id === activeTabId);
      if (currentIdx < 0) return;
      let nextIdx = currentIdx;
      if (event.key === nextKey) nextIdx = (currentIdx + 1) % tabs.length;
      else if (event.key === prevKey) nextIdx = (currentIdx - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') nextIdx = 0;
      else if (event.key === 'End') nextIdx = tabs.length - 1;
      else return;
      event.preventDefault();
      const nextTab = tabs[nextIdx];
      if (nextTab) {
        setActiveTabId(nextTab.id);
        // Move focus to the newly-selected tab so screen readers announce
        // the change. Defer to the end of the tick so the re-render lands.
        queueMicrotask(() => {
          const el = document.getElementById(tabId(nextTab.id));
          el?.focus();
        });
      }
    },
    [tabs, activeTabId, idPrefix],
  );

  // ── Bail-out: if there are no tabs, render nothing. Lets pages
  //   conditionally hide the rail (e.g. application page when the
  //   submission hasn't converted yet) without juggling a wrapper.
  if (tabs.length === 0) return null;

  // ── Glass surface tokens shared across all three layouts. ──────────
  const glass = isDark ? glassEffects.dark : glassEffects.light;
  const surfaceBg = isMaterial
    ? theme.palette.background.paper
    : isDark
      ? 'rgba(28, 28, 30, 0.78)'
      : 'rgba(255, 255, 255, 0.86)';
  const surfaceBackdrop = isMaterial ? undefined : glass.backdropBlur;
  const dividerColor = theme.palette.divider;

  // forceCollapsed (Issue #1472 PR #1477 review C4): when a parent surface
  // (e.g. <SplitPanel>) takes over the right edge, render only the
  // collapsed strip on desktop/tablet; render nothing on mobile (the FAB
  // would compete with the parent surface). Internal open-state stays
  // intact so the rail re-asserts its persisted choice when force ends.
  // Computed early so the CollapsedStrip JSX (built below) can capture it
  // for the chevron's aria-label and direction (#1472 design pass 4).
  const effectiveIsOpen = forceCollapsed ? false : isOpen;

  // ──────────────────────────────────────────────────────────────────
  // Collapsed strip — icon-only tab buttons at the right edge, desktop
  // only. v3 design round 8 (specs/00-tokens-and-invariants.md "SplitPanel
  // rail tab anatomy"): NO rotated vertical text — replaced by GlassTooltip
  // on hover. Each tab button is 32×36 (icon-only) inside the 44px-wide
  // rail strip (the 44px rail width is the a11y touch-target floor per
  // portals.md, NOT the 36px shown in the raw v3 design;
  // memory feedback_rail_width_a11y_floor).
  //
  // Active state: `alpha(primary.main, 0.14)` background + 2.5px solid
  // left-edge bar with mint glow (`boxShadow: 0 0 6px primary.main`).
  // Icon is 16px (`text.disabled` default → `primary.main` active).
  // Count badge: absolute top:3 right:3, min 14×14px, 9px mono bold,
  // `warning.main` background (inactive) / `primary.main` background
  // (active), `1.5px solid background.paper` border.
  // ──────────────────────────────────────────────────────────────────

  const mintGlow = alpha(theme.palette.primary.main, 0.14);

  const CollapsedStrip = (
    <Box
      role="complementary"
      aria-label={ariaLabel}
      sx={{
        position: 'fixed',
        top: topOffset,
        right: 0,
        bottom: 0,
        width: COLLAPSED_RAIL_WIDTH,
        zIndex: theme.zIndex.drawer - 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1,
        background: surfaceBg,
        ...(surfaceBackdrop ? { backdropFilter: surfaceBackdrop, WebkitBackdropFilter: surfaceBackdrop } : {}),
        borderLeft: `1px solid ${dividerColor}`,
      }}
    >
      {/* Collapse/expand chevron at top of strip — chevron direction tracks
          open-state (right-pointing when open = "collapse panel back"; left-
          pointing when closed = "expand panel"). 24×24 secondary control —
          NOT a touch-target floor element (the tab buttons below are; this
          chevron is supplementary and not a primary interactive surface). */}
      <IconButton
        onClick={handleToggle}
        aria-label={effectiveIsOpen ? 'Collapse context panel' : 'Expand context panel'}
        sx={{
          mb: 0.75,
          width: 24,
          height: 24,
          color: theme.palette.text.secondary,
          '&:hover': { color: theme.palette.text.primary },
          '& svg': { fontSize: 13 },
        }}
      >
        {effectiveIsOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>
      <Box
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation="vertical"
        onKeyDown={(e) => handleTabsKeyDown(e, 'vertical')}
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          width: '100%',
          alignItems: 'center',
        }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <Box
              key={tab.id}
              sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {/* Active indicator — 2.5px solid left-edge bar with mint glow.
                  Sits at left:0 of the rail strip (not the button) so it
                  flush-bleeds with the rail's inner edge. */}
              {isActive && (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 4,
                    bottom: 4,
                    width: 2.5,
                    borderRadius: '0 2px 2px 0',
                    bgcolor: 'primary.main',
                    boxShadow: `0 0 6px ${theme.palette.primary.main}`,
                  }}
                />
              )}
              {/* Icon-only button with native GlassTooltip on hover. The
                  rail's 44px width is the a11y outer hit area; the 32×36
                  button is the visual surface. Label rides on the tooltip
                  per v3 round 8 spec — no rotated vertical text. */}
              <GlassTooltip title={tab.label} placement="left" enterDelay={300}>
                <Box
                  component="button"
                  type="button"
                  id={tabId(tab.id)}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={panelId(tab.id)}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => {
                    setActiveTabId(tab.id);
                    setIsOpen(true);
                  }}
                  aria-label={tab.ariaLabel ?? tab.label}
                  sx={{
                    width: 32,
                    height: 36,
                    borderRadius: '7px',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    p: 0,
                    transition: 'background-color 120ms ease, color 120ms ease',
                    bgcolor: isActive ? mintGlow : 'transparent',
                    color: isActive ? 'primary.main' : 'text.disabled',
                    '& svg': { fontSize: 16 },
                    '&:hover': {
                      bgcolor: isActive ? mintGlow : 'action.hover',
                      color: isActive ? 'primary.main' : 'text.primary',
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {tab.icon}
                  {tab.count != null && tab.count > 0 && (
                    <Box
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        top: 3,
                        right: 3,
                        minWidth: 14,
                        height: 14,
                        px: '3px',
                        borderRadius: '7px',
                        bgcolor: isActive ? 'primary.main' : 'warning.main',
                        color: theme.palette.getContrastText(
                          isActive ? theme.palette.primary.main : theme.palette.warning.main,
                        ),
                        fontFamily: fontFamilies.mono,
                        fontSize: 9,
                        fontWeight: 700,
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1.5px solid ${theme.palette.background.paper}`,
                      }}
                    >
                      {tab.count}
                    </Box>
                  )}
                </Box>
              </GlassTooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  // ──────────────────────────────────────────────────────────────────
  // Expanded panel — uses SplitPanel chrome (header + content). The
  // active tab's label becomes the header title; a dropdown caret in
  // the header actions opens a menu to switch tabs. PR #1477 design
  // feedback: at 360px width, a horizontal tab bar gets cramped and
  // truncates labels — title+dropdown matches SplitPanel UX exactly.
  // ──────────────────────────────────────────────────────────────────

  const activeTab = tabs.find((t) => t.id === activeTabId);
  const isClosableActive = closableIds.has(activeTabId) && provider;

  const ExpandedPanel = (
    <Box
      role="complementary"
      aria-label={ariaLabel}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <SplitPanelHeader
        // Natural height — do not stretch to match the page header (the page
        // header is now variable-height with the v3 multi-line subtitle).
        sx={{ alignItems: 'center' }}
        // PR #1477 design feedback: caret moved next to the title. The
        // entire title (label + caret) is the click target — opens the
        // tab-switcher dropdown. The X stays in the actions slot so it
        // visually anchors to the right edge like every other SplitPanel.
        title={
          <Box
            component="button"
            type="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(e.currentTarget)}
            aria-label="Switch tab"
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              color: 'inherit',
              font: 'inherit',
              '&:hover': { opacity: 0.85 },
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
                borderRadius: 4,
              },
            }}
          >
            {activeTab?.label ?? ''}
            <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Box>
        }
        icon={activeTab?.icon}
        onClose={handleClose}
        actions={
          isClosableActive ? (
            <IconButton
              onClick={() => provider?.closeTab(activeTabId)}
              aria-label="Close this tab"
              sx={{ color: 'text.secondary', width: 44, height: 44, '&:hover': { color: 'text.primary' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null
        }
      />
      <GlossyMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          const showCloseButton = closableIds.has(tab.id) && provider;
          return (
            <GlossyMenuItem
              key={tab.id}
              icon={tab.icon}
              active={isActive}
              onClick={() => {
                setActiveTabId(tab.id);
                setMenuAnchor(null);
              }}
            >
              {/* Custom primary content: label + count badge + close.
                  Passing children overrides the default `label` slot; we
                  reproduce the label inline so we can lay out the count
                  badge and per-tab close button alongside it. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: isActive ? 600 : 500, flex: 1, color: 'inherit' }}>
                  {tab.label}
                </Typography>
                {tab.count != null && tab.count > 0 && (
                  <Box
                    sx={{
                      bgcolor: isActive ? 'primary.main' : 'action.selected',
                      color: isActive ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 999,
                      px: 0.75,
                      minWidth: 22,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
                      {tab.count}
                    </Typography>
                  </Box>
                )}
                {showCloseButton && (
                  <IconButton
                    edge="end"
                    aria-label={`Close ${tab.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      provider.closeTab(tab.id);
                    }}
                    sx={{ width: 36, height: 36, '& svg': { fontSize: 14 } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </GlossyMenuItem>
          );
        })}
      </GlossyMenu>
      <SplitPanelContent>
        {/* `role="region"` + `aria-label` is the right ARIA fit for a
            named section without a sibling tablist. The collapsed strip
            still uses the WAI-ARIA tabs pattern (tablist + tab + the
            same panelId/tabId wiring), so when the user expands the
            panel the screen-reader hand-off is coherent. */}
        <Box role="region" id={panelId(activeTabId)} aria-label={activeTab?.label ?? ariaLabel} tabIndex={0}>
          {dynamicContentById.has(activeTabId) ? dynamicContentById.get(activeTabId) : children(activeTabId)}
        </Box>
      </SplitPanelContent>
    </Box>
  );

  // ──────────────────────────────────────────────────────────────────
  // Mobile — full-screen drawer when open, FAB trigger when closed.
  // The drawer renders the same expanded panel; the FAB sets isOpen.
  // ──────────────────────────────────────────────────────────────────

  if (isMobile) {
    if (forceCollapsed) return null;
    if (!effectiveIsOpen) {
      return (
        <IconButton
          onClick={() => setIsOpen(true)}
          aria-label="Open context panel"
          aria-controls={panelId(activeTabId)}
          aria-expanded={false}
          sx={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            zIndex: theme.zIndex.modal,
            width: 48,
            height: 48,
            border: `1px solid ${dividerColor}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[6],
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      );
    }
    return (
      <Box
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: theme.zIndex.modal,
          background: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 1,
            borderBottom: `1px solid ${dividerColor}`,
          }}
        >
          <IconButton onClick={handleClose} aria-label="Close context panel">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden' }}>{ExpandedPanel}</Box>
      </Box>
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // Desktop/tablet — compose <SplitPanel> for the expanded surface.
  //
  // Why compose rather than re-implement: SplitPanel already owns
  // fixed-positioning, layout-offset integration, the resize handle,
  // glass tokens, framer-motion entry/exit, and the inline/overlay
  // breakpoint logic. ContextRail layers tabs + persistence + the
  // collapsed vertical strip on top. PR #1477 design feedback.
  //
  // SplitPanel needs `children` (the main content area) but ContextRail
  // is rendered as a sibling of the page content — pass an empty
  // fragment. SplitPanel's children-side compensating margin is harmless
  // when there's nothing in it; the page's own content lives one level
  // up and is sized via the `--context-rail-width` CSS var.
  // ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Vertical tab strip is ALWAYS rendered (#1472 design pass 4) — when
          the panel is open, the strip sits to its right so users can switch
          tabs without collapsing the panel. The SplitPanel below receives
          `edgeOffset={COLLAPSED_RAIL_WIDTH}` so its fixed-positioned panel
          sits at `right: 44px` instead of `right: 0`, leaving room for the
          strip. */}
      {CollapsedStrip}
      {/* Take the empty inner SplitPanel out of flow. SplitPanel renders a
          `display: flex; flex: 1` outer Box even when its children are empty
          — when the host page already has another `flex: 1` SplitPanel as a
          sibling inside `<main>`'s flex column (e.g. the loan-detail page's
          stage-map drawer), the two split the column 50/50 and the loan
          content collapses to half its proper height (#1749 follow-up bug
          surfaced 2026-05-14). Absolute-positioning the wrapper here removes
          it from flex layout entirely; SplitPanel's actual InlinePanel uses
          `position: fixed` so the open-state slide-in still anchors to the
          viewport correctly. `inset: 0` keeps the SplitPanel sized to its
          container for hit-testing on the resize handle. `pointerEvents: none`
          on the wrapper is unconditional — when the rail is open, the
          *visible* InlinePanel (role="complementary") re-enables pointer
          events via the descendant selector. The empty SplitPanel main-content
          Box (`flex: 1, overflow: auto`) inherits `none` and stops
          intercepting scroll/click on the host page beneath. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          '& [role="complementary"]': { pointerEvents: 'auto' },
        }}
      >
        <SplitPanel
          panelOpen={effectiveIsOpen}
          onPanelClose={handleClose}
          panel={ExpandedPanel}
          // Resize is allowed within [MIN, MAX]. Drag fires `onResize` and
          // we mirror the new width into `currentWidth`, which drives the
          // `--context-rail-width` CSS var so the page's right padding
          // tracks the user's drag in real time. SplitPanel persists the
          // width itself under `split-view-width-<storageKey>` — we pass
          // the scope-aware suffix so width is namespaced per business+user
          // the same way open/active-tab are (PR #1810 review C3).
          panelWidth={currentWidth}
          minPanelWidth={MIN_PANEL_WIDTH}
          maxPanelWidth={MAX_PANEL_WIDTH}
          onResize={setCurrentWidth}
          // Inline at >=1024, overlay at 768-1023. The original #1472 spec
          // pinned this at 1280, but every common windowed-browser size on
          // a 13"–15" laptop (1180–1280px viewports) is below that ceiling,
          // so the rail flipped into overlay mode and read as a "center
          // modal" — exactly the surface that ContextRail was supposed to
          // replace. 1024 keeps overlay only for genuinely narrow laptop
          // halves and tablets in landscape; full-screen drawer (<768)
          // still handles phones / portrait tablets.
          overlayBreakpoint={1024}
          mobileBreakpoint={768}
          anchor="right"
          // Inset the panel by the collapsed-strip width so the strip
          // remains visible to the right of the open panel — design pass 4.
          edgeOffset={COLLAPSED_RAIL_WIDTH}
          storageKey={splitPanelStorageKey}
          panelAriaLabel={ariaLabel}
          animate={animate}
        >
          <></>
        </SplitPanel>
      </Box>
    </>
  );
}

export default ContextRail;
